import { useChatMessages } from '@ai-sdk-tools/store';
import type { ComponentType, ReactNode } from 'react';

import { ChatDisclaimer } from './chat-disclaimer';
import { ChatInputContainer, OutlookConnectPrompt } from './chat-input';
import { ChatSettingsPanel } from './chat-settings-panel';
import { Messages, type PreviewMessageProps } from './messages';
import { getLatestPendingClarification } from '../../../lib/clarification';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { SkillsProvider } from '../contexts/skills-context';
import type { DataPartRenderer, ToolOutputRenderer } from '../lib/default-renderers';

interface ChatContentProps {
  // Pass through all props that Messages component expects
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to Messages component.
   */
  enableSourcesRendering?: boolean;
  // Props for input container
  placeholder?: string;
  toolbar?: ReactNode;
}

/**
 * Content wrapper component that decides between rendering Messages+Input or Settings panel.
 * Keeps Messages component unchanged and focused on its single responsibility.
 *
 * When settings is open, displays only the ChatSettingsPanel.
 * When settings is closed, displays the Messages component with input and disclaimer.
 */
export function ChatContent({ placeholder, toolbar, ...messagesProps }: ChatContentProps) {
  const { isSettingsOpen, currentChatAppType, outlookConnectEnabled } = useChatOverlayContext();
  const messages = useChatMessages<UIMessageWithMetadata>();

  // Show settings panel instead of messages when settings is open
  if (isSettingsOpen) {
    return <ChatSettingsPanel />;
  }

  const isOutlookChatWithoutConnect = currentChatAppType === 'OUTLOOK' && !outlookConnectEnabled;
  const hasPendingClarification = !!getLatestPendingClarification(messages);

  // Render Messages component with input and disclaimer when settings is closed
  return (
    <SkillsProvider>
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <Messages {...messagesProps} />
      </div>
      {isOutlookChatWithoutConnect ? (
        <OutlookConnectPrompt />
      ) : hasPendingClarification ? null : (
        <ChatInputContainer placeholder={placeholder} toolbar={toolbar} />
      )}
      <ChatDisclaimer />
    </SkillsProvider>
  );
}
